import React from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, AlertTriangle, ShieldCheck, Scale, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back */}
        <Link to="/">
          <Button variant="ghost" className="text-zinc-400 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2 mb-6">
            <Scale className="h-4 w-4 text-amber-500" />
            <span className="text-amber-500 font-medium text-sm">Legal</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Terms, Conditions &amp; Disclaimer</h1>
          <p className="text-zinc-400 text-lg">
            Please read these terms carefully before using this website or purchasing any products.
          </p>
          <p className="text-zinc-500 text-sm mt-2">Last updated: {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
        </div>

        <div className="space-y-8">

          {/* ── RESEARCH DISCLAIMER ── */}
          <section className="bg-amber-500/5 rounded-2xl p-6 md:p-8 border border-amber-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-500/10 rounded-lg p-2">
                <FlaskConical className="h-6 w-6 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Research Use Only Disclaimer</h2>
            </div>
            <div className="space-y-4 text-zinc-300 text-sm leading-relaxed">
              <p>
                All products listed on this website are sold <strong className="text-white">strictly for in-vitro laboratory research and scientific study purposes only</strong>. They are chemical reference standards and research compounds intended to be used by qualified researchers and professionals.
              </p>
              <p>
                These products are <strong className="text-white">NOT</strong> intended for any of the following uses:
              </p>
              <ul className="list-disc list-inside space-y-1 text-zinc-400 ml-2">
                <li>Human ingestion, injection, or consumption of any kind</li>
                <li>Animal ingestion, injection, or consumption of any kind</li>
                <li>Diagnostic, therapeutic, or medicinal purposes</li>
                <li>Use as food additives, supplements, or drugs</li>
                <li>Any use that violates applicable local, national, or international laws</li>
              </ul>
              <p>
                None of the products sold on this website have been evaluated or approved by the MHRA, FDA, EMA, or any other regulatory agency. No claims are made regarding their safety, efficacy, or suitability for any specific purpose other than qualified scientific research.
              </p>
              <p>
                By purchasing from this website, you confirm that you are a qualified researcher or are purchasing these products solely for legitimate research purposes, and that you accept full responsibility for ensuring compliance with all applicable laws and regulations in your jurisdiction.
              </p>
            </div>
          </section>

          {/* ── AGE REQUIREMENT ── */}
          <section className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-500/10 rounded-lg p-2">
                <ShieldCheck className="h-6 w-6 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Age Requirement</h2>
            </div>
            <div className="space-y-4 text-zinc-300 text-sm leading-relaxed">
              <p>
                You must be at least <strong className="text-white">18 years of age</strong> to access this website and purchase any products. By using this website, you confirm that you meet this age requirement.
              </p>
              <p>
                We reserve the right to refuse service and cancel orders from anyone we reasonably believe to be under the age of 18 or who we believe intends to misuse our products.
              </p>
            </div>
          </section>

          {/* ── LIABILITY DISCLAIMER ── */}
          <section className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-500/10 rounded-lg p-2">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Limitation of Liability</h2>
            </div>
            <div className="space-y-4 text-zinc-300 text-sm leading-relaxed">
              <p>
                Forged Supps and its operators shall not be held liable for any damages, injuries, adverse effects, or consequences of any kind arising from the misuse, improper handling, or consumption of any products purchased from this website.
              </p>
              <p>
                All products are provided "as is" for research purposes. The buyer assumes <strong className="text-white">full responsibility</strong> for the proper and lawful use of all products purchased.
              </p>
              <p>
                We do not provide medical advice, and nothing on this website should be construed as such. If you have questions about a substance, consult a qualified medical professional or licensed pharmacist.
              </p>
            </div>
          </section>

          {/* ── TERMS OF USE ── */}
          <section className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-500/10 rounded-lg p-2">
                <FileText className="h-6 w-6 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Terms of Use</h2>
            </div>
            <div className="space-y-4 text-zinc-300 text-sm leading-relaxed">
              <p><strong className="text-white">1. Acceptance of Terms</strong> — By accessing or using this website, you agree to be bound by these Terms and Conditions. If you do not agree, you must leave this website immediately.</p>

              <p><strong className="text-white">2. Product Descriptions</strong> — Product descriptions, images, and specifications are provided for informational and research reference purposes only. While we strive for accuracy, we do not guarantee that all information is complete or error-free.</p>

              <p><strong className="text-white">3. Ordering &amp; Payment</strong> — All orders are subject to acceptance and availability. We reserve the right to refuse any order at our sole discretion. Payment is required before dispatch.</p>

              <p><strong className="text-white">4. Shipping &amp; Delivery</strong> — We aim to dispatch all orders within 1–2 business days. Delivery times are estimates only and are not guaranteed. We are not responsible for delays caused by courier services, customs, or other third parties.</p>

              <p><strong className="text-white">5. Returns &amp; Refunds</strong> — Due to the nature of our products, we are unable to accept returns on opened items. If your product arrives damaged or incorrect, please contact us within 48 hours of delivery.</p>

              <p><strong className="text-white">6. Intellectual Property</strong> — All content on this website, including text, images, logos, and design, is the property of Forged Supps and is protected by applicable intellectual property laws.</p>

              <p><strong className="text-white">7. Privacy</strong> — Any personal information collected during your use of this website is handled in accordance with applicable data protection laws. We do not sell or share your personal information with third parties for marketing purposes.</p>

              <p><strong className="text-white">8. Governing Law</strong> — These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>

              <p><strong className="text-white">9. Changes to Terms</strong> — We reserve the right to modify these Terms at any time. Continued use of the website following any changes constitutes acceptance of the revised Terms.</p>
            </div>
          </section>

          {/* ── CONTACT ── */}
          <section className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800 text-center">
            <p className="text-zinc-400 text-sm mb-4">
              If you have any questions about these terms or our products, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://wa.me/447440352811" target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-600 hover:bg-green-700 text-white font-medium">
                  Contact via WhatsApp
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800 font-medium">
                  Contact Page
                </Button>
              </Link>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsPage;
